import { motion } from 'framer-motion'

const HealthCheckPhoneMockup = () => {
  return (
    <div className="relative">
      <div className="w-80 h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-2 border-gray-700">
        <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden border border-gray-800">
          <motion.div 
            className="p-5"
            animate={{ y: [0, -350, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center mb-6 text-sm font-semibold text-white">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-2xl font-bold text-white mb-1">Financial Health Check-In</div>
              <div className="text-sm text-gray-400">Weekly wellness check</div>
            </motion.div>

            {/* Streak Counter */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-4 mb-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-green-400 font-bold mb-1">Your Streak</div>
                  <div className="text-3xl font-bold text-white">7 weeks 🔥</div>
                </div>
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 10, 0],
                  }}
                  transition={{ 
                    delay: 1, 
                    duration: 0.5,
                    repeat: Infinity,
                    repeatDelay: 3
                  }}
                  className="text-5xl"
                >
                  💚
                </motion.div>
              </div>
            </motion.div>

            {/* Progress Steps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm font-bold text-white mb-4">This Week's Check-In</div>
              
              {/* Step Progress */}
              <div className="flex justify-between mb-6">
                {[1, 2, 3, 4].map((step, i) => (
                  <motion.div
                    key={step}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.0 + i * 0.1, duration: 0.3 }}
                    className="flex flex-col items-center gap-2"
                  >
                    <motion.div
                      animate={step <= 2 ? {
                        boxShadow: [
                          '0 0 0px rgba(16, 185, 129, 0)',
                          '0 0 15px rgba(16, 185, 129, 0.6)',
                          '0 0 0px rgba(16, 185, 129, 0)'
                        ]
                      } : {}}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                        step <= 2
                          ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white'
                          : 'bg-gray-800 text-gray-500'
                      }`}
                    >
                      {step <= 2 ? '✓' : step}
                    </motion.div>
                    <div className="text-xs text-gray-400 text-center">
                      {step === 1 && 'Mood'}
                      {step === 2 && 'Goals'}
                      {step === 3 && 'Habits'}
                      {step === 4 && 'Reflect'}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Current Question */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 0.5 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4"
              >
                <div className="text-sm font-bold text-white mb-3">
                  How are your spending habits this week?
                </div>
                
                {/* Emoji Slider */}
                <div className="relative">
                  <div className="flex justify-between mb-2">
                    {['😟', '😐', '🙂', '😊', '🤩'].map((emoji, i) => (
                      <motion.div
                        key={i}
                        whileHover={{ scale: 1.3 }}
                        className={`text-2xl cursor-pointer ${i === 3 ? 'scale-125' : 'opacity-50'}`}
                      >
                        {emoji}
                      </motion.div>
                    ))}
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "75%" }}
                      transition={{ delay: 2.0, duration: 0.8, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Goal Questions */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.2, duration: 0.5 }}
                className="space-y-3"
              >
                {/* Question 1 */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                      }}
                      transition={{ 
                        delay: 2.5, 
                        duration: 0.4
                      }}
                      className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold"
                    >
                      ✓
                    </motion.div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-300">Did you stick to your budget?</div>
                    </div>
                    <div className="text-lg">✅</div>
                  </div>
                </div>

                {/* Question 2 */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                      }}
                      transition={{ 
                        delay: 2.7, 
                        duration: 0.4
                      }}
                      className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold"
                    >
                      ✓
                    </motion.div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-300">Any impulse purchases?</div>
                    </div>
                    <div className="text-lg">❌</div>
                  </div>
                </div>

                {/* Question 3 - In Progress */}
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ 
                        rotate: [0, 360],
                      }}
                      transition={{ 
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      className="w-6 h-6 rounded-full border-2 border-green-400 border-t-transparent"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-green-400">Saved money this week?</div>
                    </div>
                    <div className="text-lg animate-pulse">⏳</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Insights Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.0, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm font-bold text-white mb-3">Your Progress</div>
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white mb-1">7</div>
                    <div className="text-xs text-gray-400">Weeks</div>
                  </div>
                  <div className="text-center border-l border-r border-white/10">
                    <div className="text-2xl font-bold text-green-400 mb-1">92%</div>
                    <div className="text-xs text-gray-400">Completion</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400 mb-1">+15</div>
                    <div className="text-xs text-gray-400">Health Pts</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Motivational Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.4, duration: 0.5 }}
              className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-2xl p-4"
            >
              <div className="flex gap-3">
                <div className="text-3xl">🌟</div>
                <div>
                  <div className="text-sm font-bold text-green-400 mb-1">Amazing consistency!</div>
                  <div className="text-xs text-gray-300 leading-relaxed">
                    You've checked in every week for 7 weeks. This is building a strong financial foundation!
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.8, duration: 0.5 }}
              className="mt-6"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-4 rounded-2xl shadow-lg"
              >
                Continue Check-In →
              </motion.button>
            </motion.div>

            {/* Extra spacing for scroll animation */}
            <div className="h-32"></div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default HealthCheckPhoneMockup
